import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { ModelGhost } from "oc-jaco3d/arrangement/ModelGhost";
import { Füllung } from "./Füllung";
import { Rahmen } from "./Rahmen";
import { Querleisten } from "./Querleisten";
import { quatEuler, v2, v3 } from "oc-threejs/short3";
import * as Maths from "oc-commons/Maths";
import * as Maths3 from "oc-threejs/Maths3";
import * as Commons from "oc-commons/Commons";
import { PivotKette } from "oc-jaco3d/arrangement/PivotKette";
import { ZElement } from "./ZElement";
import { BoxRuler } from "../arrangement3d/BoxRuler";
import { StücklistenIterator } from "./Stückliste";
import { Box3, Vector3 } from "three";
import { Pfosten } from "./Pfosten";
import { ghostPool } from "./Arrangement";
import { findMMR } from "../RichMetadata";
import { createDefaultClippingParamters } from "./MyModelCutted";
import { isEmpty } from "oc-commons/Commons";

const drückerHintenKey = Commons.cache((drückerVorneKey: string | undefined) => {
  if (!drückerVorneKey) {
    return null;
  }

  const drückervorne = APP.metadata.Druecker_Kombinationen.find(
    (d: any) => d.Druecker_vorne[0]?.Key == drückerVorneKey
  );
  const drückerhinten = drückervorne?.Druecker_hinten?.[0]?.Key ?? "Drücker_Klinke_edelstahl";

  return drückerhinten;
});

export type FlügelModel = ModelGhost & {
  isKloben?: boolean;
  pfosten?: Pfosten;
};

export class Flügel extends Jaco {
  static CLASS_NAME = "Flügel";

  // ----------------------------------------------------------------------------------
  // abgeleitete Werte - d.h. sind letztlich nur berechnet (in update3d) und gecached, kein Zustand
  // Achtung, diese Werte muss man immer ganz besonders im Auge haben, kann sein,
  // dass sie fälschlich doch als Zustand dienen, und das führt dann zu schwierigen Bugs
  // ----------------------------------------------------------------------------------

  element: ZElement;
  füllung: Füllung;
  rahmen: Rahmen | null = null;
  querleisten: Querleisten[] = [];
  size: Vector3 = v3();
  box: Box3 = new Box3();
  rahmenInnenBox: Box3 = new Box3();
  physicalHeight = 0;

  // fast alle Flügel-Teile ausser den Kloben und dem Anschlag sind teil des drehbarenObjekt und werden beim Tor mitgedreht
  drehbaresObjekt: PivotKette;
  motorDrehachse: PivotKette;

  torteile: ModelGhost[] = [];

  massRuler: BoxRuler = null;

  hasKloben = false;
  hasSchloss = false;
  hasAnschlagwinkel = false;
  hasTorschließer = false;

  seiteGewechselt = false;
  isÖffnungNachInnen = true;
  isAufhängungAnLinkerTorseite = true;
  öffnungsWinkel = 0;
  transitionDelay = 0;
  transitionDuration = 0;
  // physikalische Höhe der obersten Kante über dem Erdboden

  constructor(parent: Jaco, element: ZElement) {
    super(parent as any);
    this.drehbaresObjekt = new PivotKette(this);
    this.motorDrehachse = new PivotKette(this);
    this.element = element;
    this.füllung = new Füllung(this.drehbaresObjekt, this, this.element);
  }

  traverse(iterator: (j: Jaco) => void) {
    super.traverse(iterator);
    if (this.drehbaresObjekt) this.drehbaresObjekt.traverse(iterator);
    if (this.motorDrehachse) this.motorDrehachse.traverse(iterator);
    if (this.füllung) this.füllung.traverse(iterator);
    if (this.rahmen) this.rahmen.traverse(iterator);
    this.querleisten.forEach((o) => o.traverse(iterator));
    this.torteile.forEach((o) => o.traverse(iterator));
  }

  collectArtikel(
    iterator: StücklistenIterator,
    ObjectGroupTag: string | undefined = undefined,
    JacoId: number | undefined = undefined
  ) {
    this.rahmen?.collectArtikel(iterator, ObjectGroupTag, JacoId);
    this.füllung.collectArtikel(iterator, ObjectGroupTag, JacoId);

    let modelle = this.torteile;

    modelle
      .filter((model) => (model as any).stücklistenMeta)
      .forEach((model) => {
        const Breite = model.getBox().getSize(v3()).x;
        const meta = (model as any).stücklistenMeta;
        const params = { ObjektMeta: meta, ObjectGroupTag, Breite, JacoId };
        console.log("collectArtikel", params);
        iterator(params);
      });
  }

  getBox(): Box3 {
    const box = this.box.clone();
    const az = this.element.meta?.Mass.AbmessungZ || v2();
    box.min.z = az.x;
    box.max.z = az.y;
    return box;
  }

  update3d() {
    super.update3d();
    Maths3.boxFromGridMinAndSize(v3(0, 0, 0.5), this.size, this.box);
    this.updateRahmen();
    this.updateFüllung();
    this.updateQuerleisten();
    this.updateTorTeile();
    this.drehbaresObjekt.update3d();
    this.motorDrehachse.update3d();
    this.physicalHeight = this.füllung?.physicalHeight ?? 0;
  }

  /**
   * By default the first flügel of an Element might be mirrored. Additonal Condition is the existence of ModelMaterialRalation.Mirrored on ay Model.
   * @return {boolean}
   */
  get mightNeedMirroring(): boolean {
    return this.element.isDoppeltor && this.element.flügel.indexOf(this) == 0;
  }

  updateFüllung() {
    this.füllung.meta = this.element.füllungMeta;
    this.füllung.Aufbautyp = this.element.meta.Aufbautyp.Key;
    this.fitFüllungIntoRahmen();
    this.füllung.update3d();
  }

  fitFüllungIntoRahmen() {
    let padding = this.rahmen?.meta?.ModelMaterialRelation?.[0]?.Model?.Rahmeninnenabstand || 0.0;
    const paddingVector = v3(padding, padding, 0);
    this.füllung.size.copy(this.size).sub(paddingVector).sub(paddingVector);
    this.füllung.position.copy(paddingVector);
    this.rahmenInnenBox.copy(this.rahmen ? this.rahmen.innenBox : this.box);
  }

  updateRahmen() {
    const rahmenMeta = this.element.rahmenMeta;
    if (rahmenMeta) {
      if (this.rahmen == null) {
        this.rahmen = new Rahmen(this.drehbaresObjekt, this, this.element);
      }
      this.rahmen.meta = rahmenMeta;
      this.rahmen.size.copy(this.size);
      this.rahmen.position.set(0, 0, 0);
      this.rahmen.update3d();
    } else {
      this.rahmen = null;
    }
  }

  updateQuerleisten() {
    const querleistenPool = this.lazyPool(
      this.querleisten,
      () => new Querleisten(this.drehbaresObjekt, this)
    );
    const maybeQuerleisten = (thing: any) => {
      if (Commons.isEmpty(thing?.meta?.Querleistenwerte)) {
        return;
      }
      const querleisten = querleistenPool.next();
      querleisten.meta = thing.meta.Querleistenwerte[0];
    };
    this.rahmen && maybeQuerleisten(this.rahmen);
    this.füllung && maybeQuerleisten(this.füllung);
    querleistenPool.cleanup();
    this.querleisten.forEach((q) => q.update3d());
  }

  updateTorTeile() {
    const tor = this.element;
    const rahmen = this.rahmen;
    const isJumboWPC = this.füllung.Aufbautyp === "VersetzteSenkrechtprofileVariabel";
    const box = rahmen ? rahmen.box : this.getBox();
    if (tor.isAros) {
      box.max.z = 0.025;
      box.min.z = -0.025;
    }
    const torHöhe = tor.size.y;
    const posLochMitteVomDrücker = 0.245 - 0.093; //Von der unteren Kante gerechnet, maßgeblich für Positionierung
    const abstandLochmitteVonUnten =
      (torHöhe >= 1.19 ? 1.015 : torHöhe - 0.17) - posLochMitteVomDrücker;
    const abstandDrückerVomRandX = (isJumboWPC ? 0.5 : 1) * 0.015;
    const zAbstand = isJumboWPC ? 1.3 : 0.9 + (box.max.z - box.min.z);
    const [rahmenGridX, drückerScaleX] = this.isAufhängungAnLinkerTorseite ? [1, -1] : [0, 1];

    this.torteile = ghostPool(this).clearInstances(this.torteile);

    const addTorteil = (objectKey: string, parent: Jaco) => {
      const mmr = findMMR(objectKey);
      if (!mmr) {
        return null;
      }
      const teil = ghostPool(this).getForModelMaterialRelation(
        parent,
        mmr,
        createDefaultClippingParamters()
      );
      (teil as any).modelMaterialRelation = mmr;
      this.torteile.push(teil);
      return teil;
    };

    const addSchloss = () => {
      [
        {
          // vorne
          drückerKey: tor.drückerAuswahlwert?.Key,
          scaleZ: 1,
          rahmenGridZ: 1,
          zm: -1 * zAbstand,
        },
        {
          // hinten
          drückerKey: drückerHintenKey(tor.drückerAuswahlwert?.Key),
          scaleZ: -1,
          rahmenGridZ: 0,
          zm: 1 * zAbstand,
        },
      ].forEach(({ drückerKey, scaleZ, rahmenGridZ, zm }) => {
        const drücker = addTorteil(drückerKey, this.drehbaresObjekt);
        if (!drücker) {
          return;
        }
        const drückerBox = drücker.getBox();
        drücker.position
          .copy(box.grid(rahmenGridX, 0, rahmenGridZ))
          .add(
            v3(
              abstandDrückerVomRandX * drückerScaleX,
              abstandLochmitteVonUnten,
              zm * drückerBox.grid(0, 0, 0).z
            )
          );
        drücker.scale.set(drückerScaleX, 1, scaleZ);

        if (tor.profilzylinderAuswahlwert?.Key == "Profilzylinder_mit") {
          const zylinder = addTorteil("Profilzylinder", this.drehbaresObjekt);
          zylinder.position
            .copy(drücker.position)
            .add(
              v3(
                0.0062 * drückerScaleX,
                posLochMitteVomDrücker - 0.116,
                zm * (isJumboWPC ? 0.0235 : 0.0325)
              )
            );
          zylinder.scale.set(drückerScaleX, 1, scaleZ);
        }
      });
    };

    const addAnschlagWinkelUndStulpfalle = () => {
      const px = drückerScaleX * 0.015;
      const py = abstandLochmitteVonUnten - 0.05;

      const rahmenPos = box.grid(rahmenGridX, 0, 0.5);
      const stulpFalle = addTorteil("Stulpfalle", this.drehbaresObjekt);
      stulpFalle.position.copy(rahmenPos).add(v3(px, py + 0.3, 0));
      stulpFalle.scale.set(-drückerScaleX, -1, 1);

      const anschlagWinkel = addTorteil("Anschlagwinkel", this);
      (anschlagWinkel as any).isAnschlagWinkel = true;
      anschlagWinkel.scale.set(drückerScaleX, 1, this.isÖffnungNachInnen ? -1 : 1);
      anschlagWinkel.position.copy(rahmenPos).add(v3(-px, py, 0));
    };

    const addKloben = () => {
      const pfosten = this.isAufhängungAnLinkerTorseite ? tor.pfostenLinks : tor.pfostenRechts;
      if (!pfosten) {
        return;
      }
      [
        { yg: 0, yo: 0.205 },
        { yg: 1, yo: -0.205 },
      ].forEach(({ yg, yo }) => {
        const xf = this.isAufhängungAnLinkerTorseite ? -1 : 1;
        const zf = this.isÖffnungNachInnen ? -1 : 1;
        const scale = v3(xf, 1, -zf);

        const position = box
          .grid(this.isAufhängungAnLinkerTorseite ? 0 : 1, yg, 0.5)
          .add(v3(0, yo + 0.04, 0.02 * zf));

        const anMauer = pfosten.anMauer;

        let klobenDrehAchsePosition;

        if (anMauer) {
          const schanier = addTorteil(
            "Tuerband_Gerade_Mit_Schanier_Schraube",
            this.drehbaresObjekt
          );
          (schanier as FlügelModel).isKloben = true;
          schanier.position.copy(position).add(v3(0.0, 0, 0.015 * -zf));
          schanier.scale.copy(scale);
          (schanier as FlügelModel).pfosten = pfosten;

          klobenDrehAchsePosition = v3(-0.0194 * xf, 0, 0.079 * zf).add(position);

          // visualisieren der Drehachse:
          // const p = new OcAxisHelper(2.5);
          // p.position.copy(klobenDrehAchsePosition);
        } else {
          const beschlagsatz = this.element.getBeschlagsatzFürTor(
            this.element.getPfosten()[this.isAufhängungAnLinkerTorseite ? 0 : 1]
          );
          const klobenObjectKey =
            beschlagsatz?.Beschlagsatz_Kombinationen[0]?.Kloben?.[0]?.Key ?? "Kloben_L";
          const kloben = addTorteil(klobenObjectKey, this);
          (kloben as any).isKloben = true;
          kloben.position.copy(position);
          kloben.scale.copy(scale);
          (kloben as any).pfosten = pfosten;

          const schanierObjectKey =
            beschlagsatz?.Beschlagsatz_Kombinationen[0]?.Schanier?.[0]?.Key ?? "Schanier_L";
          const schanier = addTorteil(schanierObjectKey, this.drehbaresObjekt);
          schanier.position.copy(position);
          schanier.scale.copy(scale);

          klobenDrehAchsePosition = v3(0.055 * xf, 0, 0.04 * zf).add(position);
        }

        this.drehbaresObjekt.transition = {
          duration: this.transitionDuration,
          delay: this.transitionDelay,
          property: "quaternion",
        };
        this.drehbaresObjekt.rotateAround(
          klobenDrehAchsePosition,
          Maths3.vector.y,
          (this.öffnungsWinkel * Math.PI) / 180
        );
      });
    };

    const addTorschließer = () => {
      const pfosten = this.isAufhängungAnLinkerTorseite ? tor.pfostenRechts : tor.pfostenLinks;
      if (!pfosten) {
        return;
      }
      const yg = 1;
      const yo = -0.05;
      const xf = this.isAufhängungAnLinkerTorseite ? -1 : 1;
      const zf = this.isÖffnungNachInnen ? -1 : 1;
      const scale = v3(xf, 1, zf);

      const position = box
        .grid(this.isAufhängungAnLinkerTorseite ? 0 : 1, yg, 0.5)
        .add(v3(0, yo, 0.08 * zf));

      const torschließerHalterung = addTorteil("Torschließer_Halterung", this);
      torschließerHalterung.position.copy(position).add(v3(xf * 0.0335, -0.065, -0.02 * zf));
      torschließerHalterung.scale.copy(scale);

      const torschließerMechanik = addTorteil("Torschließer_Mechanik", this.drehbaresObjekt);
      torschließerMechanik.position.copy(position).add(v3(xf * -0.0875, 0, -0.0315 * zf));
      torschließerMechanik.scale.copy(scale);

      const torschließerDrehAchsePosition = v3(0.0555 * xf, 0, -0.02 * zf).add(position);

      this.drehbaresObjekt.transition = {
        duration: this.transitionDuration,
        delay: this.transitionDelay,
        property: "quaternion",
      };
      this.drehbaresObjekt.rotateAround(
        torschließerDrehAchsePosition,
        Maths3.vector.y,
        (this.öffnungsWinkel * Math.PI) / 180
      );
    };

    const addEAntrieb = () => {
      const contract = () =>
        (this.isAufhängungAnLinkerTorseite ? tor.pfostenRechts : tor.pfostenLinks) &&
        tor.isDoppeltor;
      if (!contract()) {
        return;
      }

      const yo = 0;
      const xf = this.isAufhängungAnLinkerTorseite ? 1 : -1;
      const zf = this.isÖffnungNachInnen ? 1 : -1;
      const scale = v3(xf, 1, -zf);

      const position = box
        .grid(this.isAufhängungAnLinkerTorseite ? 0 : 1, 0, 0.5)
        .add(v3(xf * -0.215, yo - 0.06, -0.055 * zf));

      const { antrieb } = this.element.eAntriebInfo;
      const halterung = addTorteil(antrieb.Auswahlwert.Objekt[0].Key, this);
      halterung.position.copy(position);
      halterung.scale.copy(scale);

      const motor = addTorteil(antrieb.Auswahlwert.Objekt[1].Key, this.motorDrehachse);
      motor.quaternion.copy(quatEuler(0, xf * 3, 0));
      motor.position.copy(position).add(v3(xf * 0.0135, yo + 0.05, -0.09 * zf));
      motor.scale.copy(scale);

      const motorDrehAchsePosition = v3(0.0145 * xf, 0.125, -0.125 * zf).add(position);

      // visualisieren der Drehachse:
      // const p = new OcAxisHelper(2.5);
      // p.position.copy(motorDrehAchsePosition);
      // this.motorDrehachse?.jaco3d?.add(p);

      this.motorDrehachse.transition = {
        duration: this.transitionDuration,
        delay: this.transitionDelay,
        property: "quaternion",
      };
      this.motorDrehachse.rotateAround(
        motorDrehAchsePosition,
        Maths3.vector.y,
        (this.öffnungsWinkel * Math.PI) / 180
      );

      const bodenverriegelungsSetAuswahlwertKey = "Elektrisches Bodenverriegelungs-Set";
      const eAntriebZubehörType = "E_Antrieb_Zubehör";

      const flügelIndex = this.element.flügel.indexOf(this);
      const gehflügelIndex = this.element.isGehflügelLinks ? 1 : 0;
      const isGehflügel = flügelIndex >= 0 && flügelIndex === gehflügelIndex;
      const bodenverriegelungssetAnzahl =
        this.element.zubehörAnzahl[bodenverriegelungsSetAuswahlwertKey] ?? 0;

      if (isGehflügel && bodenverriegelungssetAnzahl > 0) {
        const zubehörMeta = APP.metadata.Zubehoer.find(
          (z) =>
            z?.Type === eAntriebZubehörType &&
            z?.Auswahlwert?.Key === bodenverriegelungsSetAuswahlwertKey
        );
        const zubehörObjektKey = zubehörMeta?.Key;
        if (!Commons.isEmpty(zubehörObjektKey)) {
          const verriegelung = addTorteil(zubehörObjektKey, this.drehbaresObjekt);
          if (verriegelung) {
            const x = !this.element.isGehflügelLinks ? this.size.x : 0;
            verriegelung.position.copy(v3(x - 0.04 * xf, -this.element.positionY, -0.02 * zf));
            verriegelung.scale.copy(scale);
          }
        }
      }
    };

    if (this.hasSchloss) {
      addSchloss();
    }

    if (this.hasAnschlagwinkel) {
      addAnschlagWinkelUndStulpfalle();
    }

    if (this.hasKloben) {
      addKloben();
    } else {
      this.drehbaresObjekt.transition = undefined;
      this.drehbaresObjekt.reset();
    }

    if (this.hasTorschließer) {
      addTorschließer();
    }

    if (this.element.isEAntrieb) {
      addEAntrieb();
    }

    this.torteile.forEach((q) => q.update3d());
  }

  get heightPhysical() {
    return (
      _.max([this.füllung, this.rahmen].filter((m) => m != null).map((m) => m.heightPhysical)) || 0
    );
  }

  get isÖffnungInSegmentNormale() {
    return this.isAufhängungAnLinkerTorseite != this.isÖffnungNachInnen;
  }
}
